/**
 * 再オンボーディング判定エンジン (Task C3)
 *
 * decideReonboarding: Pure function — ユーザー情報・現行バージョン・現在時刻から
 *   再オンボーディング種別を返す。単体テスト可能。
 *
 * useReonboardingDecision: React hook — ログイン直後に 1 度だけ判定し、
 *   decision.redirectTo があれば ReonboardingGate がナビゲーションを実施する。
 *
 * 優先順位:
 *   1. onboarding_completed_at IS NULL → initial_full
 *   2. onboarding_version < current    → version_diff
 *   3. last_login > 30 日前           → long_absence
 *   4. plan_changed_at > last_login_at → plan_changed
 *   5. それ以外                       → none
 */

import { useEffect, useMemo, useRef } from 'react';
import { useUser } from '../stores/appStore';

export const ONBOARDING_VERSION_CURRENT = '1.0';
export const LONG_ABSENCE_THRESHOLD_DAYS = 30;

export type ReonboardingKind =
  | 'initial_full'
  | 'version_diff'
  | 'long_absence'
  | 'plan_changed'
  | 'none';

export interface ReonboardingDecision {
  kind: ReonboardingKind;
  /** リダイレクト先 (initial_full 時のみ '/onboarding'). それ以外は null */
  redirectTo: string | null;
}

export interface UserShape {
  onboarding_completed_at: string | null | undefined;
  onboarding_version: string | null | undefined;
  last_login_at: string | null | undefined;
  plan_changed_at: string | null | undefined;
}

/**
 * Pure function: ユーザー情報から再オンボーディング種別を判定する.
 * now を引数で受け取るためテスト可能。
 */
export function decideReonboarding(
  user: UserShape,
  currentVersion: string,
  now: Date,
): ReonboardingDecision {
  // 1. 初回フル (未完了)
  if (!user.onboarding_completed_at) {
    return { kind: 'initial_full', redirectTo: '/onboarding' };
  }

  // 2. version 差分
  const userVersion = user.onboarding_version ?? '0.0';
  if (compareVersions(userVersion, currentVersion) < 0) {
    return { kind: 'version_diff', redirectTo: null };
  }

  // 3. 長期不在 (> 30 日)
  if (user.last_login_at) {
    const lastLogin = new Date(user.last_login_at);
    const diffDays = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > LONG_ABSENCE_THRESHOLD_DAYS) {
      return { kind: 'long_absence', redirectTo: null };
    }
  }

  // 4. プラン変更
  if (user.plan_changed_at && user.last_login_at) {
    if (new Date(user.plan_changed_at) > new Date(user.last_login_at)) {
      return { kind: 'plan_changed', redirectTo: null };
    }
  }

  return { kind: 'none', redirectTo: null };
}

/**
 * Semver 簡易比較 ('0.9' < '1.0' など).
 * Returns: -1 (a < b), 0 (a === b), 1 (a > b)
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10));
  const pb = b.split('.').map((n) => parseInt(n, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}

/**
 * ログイン直後に 1 度だけ実行される判定フック.
 * ReonboardingGate がこの decision を受けてリダイレクト判定を行う.
 */
export function useReonboardingDecision(): ReonboardingDecision {
  const user = useUser();
  const decidedRef = useRef(false);

  const decision = useMemo((): ReonboardingDecision => {
    if (!user) return { kind: 'none', redirectTo: null };
    return decideReonboarding(
      {
        onboarding_completed_at: (user as unknown as UserShape).onboarding_completed_at ?? null,
        onboarding_version: (user as unknown as UserShape).onboarding_version ?? null,
        last_login_at: (user as { lastLoginAt?: string }).lastLoginAt ?? null,
        plan_changed_at: (user as unknown as UserShape).plan_changed_at ?? null,
      },
      ONBOARDING_VERSION_CURRENT,
      new Date(),
    );
  }, [user]);

  useEffect(() => {
    if (!user || decidedRef.current) return;
    decidedRef.current = true;
  }, [user, decision]);

  return decision;
}
